import { Clock, Database, Layers } from 'lucide-react';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { formatBytes, formatNumber } from '../../lib/data-catalog-format.js';
import type {
  CatalogDataset,
  FeatureMapping,
  FirestoreCollectionMeta,
} from '../../types/data-catalog.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 機能タブ選択時の詳細表示。選択された機能が使用する BQ テーブル / Firestore コレクションを一覧し、データフロー概要を表示する。
 * @graph-connects none
 */
export function FeatureDetailView({
  feature,
  datasets,
  collections,
}: {
  feature: FeatureMapping;
  datasets: CatalogDataset[];
  collections: FirestoreCollectionMeta[];
}) {
  const bqResources = feature.bqTables
    .map((ref) => {
      const [dsId, tblId] = ref.split('.');
      const ds = datasets.find((d) => d.datasetId === dsId);
      const table = ds?.tables.find((t) => t.tableId === tblId);
      return table ? { datasetId: dsId, table } : null;
    })
    .filter((r): r is { datasetId: string; table: CatalogDataset['tables'][number] } => r !== null);

  const fsResources = feature.firestoreCollections
    .map((name) => collections.find((c) => c.shortName === name))
    .filter((c): c is FirestoreCollectionMeta => c != null);

  return (
    <div className="mx-auto max-w-[900px] space-y-6">
      <div>
        <h2 className="text-lg font-bold text-[var(--color-text-primary)]">{feature.label}</h2>
        <p className="mt-1 text-xs text-[var(--color-text-muted)]">{feature.dataFlowDescription}</p>
      </div>

      {feature.pipeline && (
        <div className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
          <Clock className="h-3.5 w-3.5" />
          <span className="font-medium">{feature.pipeline.name}</span>
          <span className="text-[var(--color-text-muted)]">— {feature.pipeline.schedule}</span>
          <span className="rounded bg-[var(--color-bg-base)] px-1.5 py-0.5 text-[10px]">
            {feature.pipeline.writeMode}
          </span>
        </div>
      )}

      {bqResources.length > 0 && (
        <div>
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-[var(--color-text-primary)]">
            <Database className="h-4 w-4 text-[var(--color-accent-primary)]" />
            BigQuery ({bqResources.length} tables)
          </h3>
          <div className="space-y-2">
            {bqResources.map(({ datasetId, table }) => (
              <Card key={`${datasetId}.${table.tableId}`} className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-[var(--color-text-primary)]">
                      {datasetId}.{table.tableId}
                    </span>
                    <span
                      className={`rounded-full px-1.5 py-0 text-[9px] font-medium ${
                        table.tableType === 'VIEW'
                          ? 'bg-[var(--color-success)]/10 text-[var(--color-success)]'
                          : 'bg-[var(--color-accent-primary)]/10 text-[var(--color-accent-primary)]'
                      }`}
                    >
                      {table.tableType}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-[var(--color-text-muted)]">
                    <span>{formatNumber(table.rowCount)} 行</span>
                    <span>{formatBytes(table.sizeBytes)}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {fsResources.length > 0 && (
        <div>
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-[var(--color-text-primary)]">
            <Layers className="h-4 w-4 text-[var(--color-warning)]" />
            Firestore ({fsResources.length} collections)
          </h3>
          <div className="space-y-2">
            {fsResources.map((coll) => (
              <Card key={coll.shortName} className="p-3">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm text-[var(--color-text-primary)]">
                    {coll.collectionPath}
                  </span>
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {formatNumber(coll.docCount)} docs
                  </span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {bqResources.length === 0 && fsResources.length === 0 && (
        <Card className="p-6 text-center text-sm text-[var(--color-text-muted)]">
          この機能に紐付くリソースはありません
        </Card>
      )}
    </div>
  );
}
